import { getAddBooksData } from '../services/getAddBooksData';

const normalize = (s = '') =>
	s
		.toString()
		.normalize('NFKD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase()
		.trim();

const tokenize = (s = '') =>
	normalize(s)
		.split(/[^a-z0-9]+/g)
		.filter(Boolean);

/**
 * Improved addBooksData:
 * - Searches in title + abstract + words
 * - Supports multi-word queries
 * - Prefers stronger matches (title > words > abstract)
 * - Avoids duplicates
 * - Efficient: precomputes normalized fields once per call (no nested loops per word)
 */
export const addBooksData = (data, queryFetched, institutionCode) => {
	const books = getAddBooksData(institutionCode) || [];
	if (!books.length) return data;

	const q = normalize(queryFetched);
	if (!q) return data;

	const qTokens = new Set(tokenize(q));
	if (qTokens.size === 0) return data;

	const existingUrls = new Set((data || []).map((x) => x?.url).filter(Boolean));

	// Score matches and then prepend best matches
	const matches = [];

	for (const b of books) {
		if (b?.url && existingUrls.has(b.url)) continue;

		const titleN = normalize(b.title);
		const abstractN = normalize(b.abstract);
		const wordsN = normalize((b.words || []).join(' '));

		// Fast phrase contains
		const titleHasPhrase = titleN.includes(q);
		const wordsHasPhrase = wordsN.includes(q);
		const abstractHasPhrase = abstractN.includes(q);

		// Token overlap (for multi-word queries)
		// NOTE: we only tokenize fields if needed (to reduce work)
		let tokenHits = 0;

		if (!titleHasPhrase && !wordsHasPhrase && !abstractHasPhrase) {
			// If no phrase hit, compute token overlap
			const fieldTokens = new Set([
				...tokenize(titleN),
				...tokenize(wordsN),
				...tokenize(abstractN),
			]);

			for (const t of qTokens) {
				if (fieldTokens.has(t)) tokenHits++;
			}

			// Require at least one token match
			if (tokenHits === 0) continue;
		} else {
			// phrase match implies relevance; still compute tokenHits cheaply for ranking
			// (tokenize query only; don't tokenize fields again)
			for (const t of qTokens) {
				if (titleN.includes(t) || wordsN.includes(t) || abstractN.includes(t))
					tokenHits++;
			}
		}

		// Ranking: title phrase > title tokens > words phrase > words tokens > abstract phrase > abstract tokens
		let score = 0;
		if (titleHasPhrase) score += 1000;
		if (wordsHasPhrase) score += 600;
		if (abstractHasPhrase) score += 300;

		// token hits matter, but less than phrase
		score += tokenHits * 50;

		// bonus for exact title match
		if (titleN === q) score += 1500;

		matches.push({ score, book: b });
	}

	if (!matches.length) return data;

	matches.sort((a, b) => b.score - a.score);

	// Prepend matches, keep original order for existing data
	const toPrepend = matches.map((m) => m.book);
	return [...toPrepend, ...(data || [])];
};
